import { useCallback, useMemo } from "react";

import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import useTranslations from "@/shared/lang/useTranslations";

import type { LedgerLabels } from "../hooks/useLedgerLabels";
import { LedgerDirection, type LedgerAccountRollupRow } from "../types/paymentsLedger.types";
import { LedgerAccountCell } from "./LedgerAccountCell";

export interface LedgerAccountRollupProps {
  rows: LedgerAccountRollupRow[];
  labels: LedgerLabels;
  isMobile: boolean;
}

const AMOUNT_FRACTIONS = { minimumFractionDigits: 2, maximumFractionDigits: 8 } as const;

/** The side is rendered as a label, so the amount itself is always shown unsigned. */
const netMagnitude = (row: LedgerAccountRollupRow) => (row.net.startsWith("-") ? row.net.slice(1) : row.net);

/**
 * Net effect of the whole operation on each (account, currency) pair — reversals included, which is exactly
 * why it matters: an operation that reserved and then released funds shows a flat net, while its entry list
 * shows two movements.
 *
 * The net is `debits − credits`; its sign is rendered as the SIDE the account closes on, not as a plus or
 * minus, because the meaning of a sign depends on the account type. `Saldo final` is the account's own
 * `balance_after` at the last leg of this operation, taken from the backend and never recomputed.
 */
export function LedgerAccountRollup({ rows, labels, isMobile }: LedgerAccountRollupProps) {
  const { t, activeLanguage } = useTranslations("paymentsLedger");

  const netSide = useCallback(
    (row: LedgerAccountRollupRow) =>
      row.netSide === LedgerDirection.DEBIT
        ? t("net_side_debit")
        : row.netSide === LedgerDirection.CREDIT
          ? t("net_side_credit")
          : t("net_flat"),
    [t],
  );

  const columns = useMemo<ColocaDataTableColumn<LedgerAccountRollupRow>[]>(
    () => [
      {
        id: "account",
        header: t("th_account"),
        cell: (row) => <LedgerAccountCell account={row.account} labels={labels} />,
      },
      {
        id: "debit",
        header: t("totals_debit"),
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (row) => (
          <ColocaAmountDisplay
            value={row.debit}
            currency={row.currency}
            size="sm"
            locale={activeLanguage}
            {...AMOUNT_FRACTIONS}
          />
        ),
      },
      {
        id: "credit",
        header: t("totals_credit"),
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (row) => (
          <ColocaAmountDisplay
            value={row.credit}
            currency={row.currency}
            size="sm"
            locale={activeLanguage}
            {...AMOUNT_FRACTIONS}
          />
        ),
      },
      {
        id: "net",
        header: t("th_net"),
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (row) => (
          <span className="inline-flex flex-col items-end gap-0.5">
            <ColocaAmountDisplay
              value={netMagnitude(row)}
              currency={row.currency}
              size="sm"
              locale={activeLanguage}
              {...AMOUNT_FRACTIONS}
            />
            <ColocaBadge variant={row.netSide ? "default" : "secondary"} className="font-sans">
              {netSide(row)}
            </ColocaBadge>
          </span>
        ),
      },
      {
        id: "balance",
        header: t("th_last_balance"),
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (row) => (
          <ColocaAmountDisplay
            value={row.lastBalanceAfter}
            size="sm"
            locale={activeLanguage}
            className="text-muted-foreground"
            {...AMOUNT_FRACTIONS}
          />
        ),
      },
    ],
    [activeLanguage, labels, netSide, t],
  );

  if (isMobile) {
    return (
      <div className="divide-y divide-border/60 rounded-xl border border-border bg-card">
        {rows.map((row) => (
          <div key={row.key} className="space-y-2 p-4">
            <LedgerAccountCell account={row.account} labels={labels} />
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-xs text-muted-foreground">{t("th_net")}</span>
              <span className="inline-flex items-baseline gap-2">
                <ColocaAmountDisplay
                  value={netMagnitude(row)}
                  currency={row.currency}
                  size="sm"
                  locale={activeLanguage}
                  {...AMOUNT_FRACTIONS}
                />
                <ColocaBadge variant={row.netSide ? "default" : "secondary"} className="font-sans">
                  {netSide(row)}
                </ColocaBadge>
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-xs text-muted-foreground">{t("th_last_balance")}</span>
              <ColocaAmountDisplay
                value={row.lastBalanceAfter}
                size="xs"
                locale={activeLanguage}
                className="text-muted-foreground"
                {...AMOUNT_FRACTIONS}
              />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return <ColocaDataTable columns={columns} rows={rows} rowKey={(row) => row.key} />;
}
